import { z } from "zod";
import { conversationExecutionSchema } from "./conversations.js";
import { appIdSchema, correlationIdSchema, eventIdSchema, isoTimestampSchema, runIdSchema, threadIdSchema, userIdSchema, } from "./identifiers.js";
import { agentCommandSchema } from "./runs.js";
import { agentSkillCatalogIdentity, agentSkillCatalogIdentitySchema, } from "./skills.js";
export const AGENT_TRACE_FORMAT = "agent-run-bundle-v1";
export const AGENT_TRACE_SCHEMA_VERSION = 1;
export const AGENT_TRACE_COMPACTION_VERSION = 2;
export const AGENT_TRACE_MEDIA_TYPE = "application/vnd.opencloud.agent-run-bundle+json";
export const agentTraceJsonSchema = z.lazy(() => z.union([
    z.null(),
    z.boolean(),
    z.number().finite(),
    z.string(),
    z.array(agentTraceJsonSchema),
    z.record(z.string(), agentTraceJsonSchema),
]));
export const agentTraceSha256DigestSchema = z
    .string()
    .regex(/^sha256:[a-f0-9]{64}$/);
const traceIdentifierSchema = z.string().trim().min(1).max(500);
const traceReasoningEffortSchema = z.enum([
    "none",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
]);
export const agentTraceStatusSchema = z.enum([
    "collecting",
    "complete",
    "incomplete",
    "corrupt",
]);
export const agentTraceTerminationSchema = z
    .object({
    kind: z.enum([
        "crash",
        "oom_killed",
        "callback_lost",
        "launcher_restart",
        "unrecoverable",
    ]),
    observedAt: isoTimestampSchema,
    reason: z.string().trim().min(1).max(500),
    expectedThroughSequence: z.number().int().nonnegative().nullable(),
})
    .strict();
export const agentTraceInputIdentitySchema = z
    .object({
    kind: z.enum([
        "ai-sdk",
        "harness-adapter",
        "codex",
        "runtime-image",
        "skill-catalog",
        "tool-registry",
        "prompt",
        "policy",
    ]),
    name: z.string().trim().min(1).max(200),
    version: z.string().trim().min(1).max(200).nullable(),
    digest: agentTraceSha256DigestSchema,
    source: z.enum([
        "runtime-package",
        "runtime-image",
        "harness-definition",
        "run-snapshot",
    ]),
})
    .strict();
export function missingAgentTraceInputIdentities(inputs) {
    const requirements = [
        {
            label: "ai-sdk:ai",
            matches: (input) => input.kind === "ai-sdk" &&
                input.name === "ai" &&
                input.version !== null &&
                input.source === "runtime-package",
        },
        {
            label: "ai-sdk:@ai-sdk/harness",
            matches: (input) => input.kind === "ai-sdk" &&
                input.name === "@ai-sdk/harness" &&
                input.version !== null &&
                input.source === "runtime-package",
        },
        {
            label: "codex:@openai/codex+executable",
            matches: (input) => input.kind === "codex" &&
                input.name === "@openai/codex" &&
                input.version !== null &&
                input.source === "runtime-package",
        },
        {
            label: "runtime-image",
            matches: (input) => input.kind === "runtime-image" && input.source === "runtime-image",
        },
        {
            label: "harness-adapter",
            matches: (input) => input.kind === "harness-adapter" &&
                input.source === "harness-definition",
        },
        {
            label: "skill-catalog",
            matches: (input) => input.kind === "skill-catalog" && input.source === "run-snapshot",
        },
        {
            label: "tool-registry",
            matches: (input) => input.kind === "tool-registry" &&
                input.source === "harness-definition",
        },
        {
            label: "prompt",
            matches: (input) => input.kind === "prompt" && input.source === "harness-definition",
        },
        {
            label: "policy",
            matches: (input) => input.kind === "policy" && input.source === "harness-definition",
        },
    ];
    return requirements
        .filter((requirement) => !inputs.some(requirement.matches))
        .map(({ label }) => label);
}
/** The largest workspace content artifact, inline or stored. */
export const MAX_TRACE_CONTENT_BYTES = 8 * 1024 * 1024;
const MAX_TRACE_CONTENT_CHARACTERS = 12 * 1024 * 1024;
const CANONICAL_BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
function canonicalTraceContent(value, context) {
    if (value.encoding === "base64" &&
        (value.content.length % 4 !== 0 || !CANONICAL_BASE64.test(value.content))) {
        context.addIssue({
            code: "custom",
            path: ["content"],
            message: "Trace artifact content is not canonical base64",
        });
    }
}
export const agentTraceContentArtifactSchema = z
    .object({
    digest: agentTraceSha256DigestSchema,
    byteSize: z.number().int().nonnegative().max(MAX_TRACE_CONTENT_BYTES),
    mediaType: z.string().trim().min(1).max(200),
    encoding: z.enum(["utf8", "base64"]),
    content: z.string().max(MAX_TRACE_CONTENT_CHARACTERS),
    redacted: z.boolean(),
})
    .strict()
    .superRefine(canonicalTraceContent);
/** Content stored once per user and app beside the traces that use it. */
export const AGENT_TRACE_CONTENT_STORAGE = "trace-content";
/**
 * A workspace content artifact whose sanitized bytes live in the protected
 * trace store, once per user and app, instead of inline. The digest binds the
 * stored bytes; `encoding` is how an inline or resolved copy represents them.
 * Only traces whose manifest sets `capture.contentByReference` use this form.
 */
export const agentTraceStoredContentArtifactSchema = z
    .object({
    digest: agentTraceSha256DigestSchema,
    byteSize: z.number().int().nonnegative().max(MAX_TRACE_CONTENT_BYTES),
    mediaType: z.string().trim().min(1).max(200),
    encoding: z.enum(["utf8", "base64"]),
    redacted: z.boolean(),
    storage: z.literal(AGENT_TRACE_CONTENT_STORAGE),
})
    .strict();
/** Stored content returned with a trace download, keyed by digest. */
export const agentTraceResolvedContentSchema = z
    .object({
    encoding: z.enum(["utf8", "base64"]),
    content: z.string().max(MAX_TRACE_CONTENT_CHARACTERS),
})
    .strict()
    .superRefine(canonicalTraceContent);
const agentTraceWorkspacePathSchema = z
    .string()
    .min(1)
    .max(4_096)
    .refine((value) => !value.startsWith("/") &&
    !value.includes("\\") &&
    value.split("/").every((part) => part.length > 0 && part !== "." && part !== ".."), "Workspace evidence paths must be normalized and relative");
export const agentTraceWorkspaceFileSchema = z
    .object({
    path: agentTraceWorkspacePathSchema,
    kind: z.enum(["file", "symlink"]),
    byteSize: z.number().int().nonnegative(),
    digest: agentTraceSha256DigestSchema.nullable(),
    contentRef: agentTraceSha256DigestSchema.nullable(),
    availability: z.enum([
        "captured",
        "metadata_only",
        "policy_excluded",
    ]),
    reason: z.string().trim().min(1).max(500).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.availability === "captured" &&
        (!value.digest || value.contentRef !== value.digest)) {
        context.addIssue({
            code: "custom",
            path: ["contentRef"],
            message: "Captured workspace files require their content digest reference",
        });
    }
    if (value.availability !== "captured" &&
        (value.contentRef !== null || value.reason === null)) {
        context.addIssue({
            code: "custom",
            path: ["availability"],
            message: "Unavailable workspace content requires an explicit reason",
        });
    }
});
export const agentTraceWorkspaceSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    kind: z.enum(["start", "recovery", "end"]),
    workspaceRoot: z.literal("/workspace"),
    previousTreeDigest: agentTraceSha256DigestSchema.nullable(),
    treeDigest: agentTraceSha256DigestSchema,
    fileCount: z.number().int().nonnegative(),
    capturedFileCount: z.number().int().nonnegative(),
    capturedBytes: z.number().int().nonnegative(),
    files: z.array(agentTraceWorkspaceFileSchema).max(50_000),
    omitted: z
        .array(z
        .object({
        path: agentTraceWorkspacePathSchema,
        reason: z.string().trim().min(1).max(500),
    })
        .strict())
        .max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    const capturedFiles = value.files.filter((file) => file.availability === "captured");
    if (value.fileCount !== value.files.length ||
        value.capturedFileCount !== capturedFiles.length) {
        context.addIssue({
            code: "custom",
            path: ["fileCount"],
            message: "Workspace snapshot counts do not match its file inventory",
        });
    }
    const paths = value.files.map((file) => file.path);
    if (new Set(paths).size !== paths.length ||
        paths.some((entry, index) => index > 0 && entry <= paths[index - 1])) {
        context.addIssue({
            code: "custom",
            path: ["files"],
            message: "Workspace snapshot paths must be unique and sorted",
        });
    }
    if ((value.kind === "recovery") !== Boolean(value.previousTreeDigest)) {
        context.addIssue({
            code: "custom",
            path: ["previousTreeDigest"],
            message: "Only a recovery snapshot identifies the previous tree",
        });
    }
    const omittedPaths = value.omitted.map(({ path }) => path);
    if (new Set(omittedPaths).size !== omittedPaths.length ||
        omittedPaths.some((entry, index) => index > 0 && entry <= omittedPaths[index - 1]) ||
        omittedPaths.some((entry) => paths.includes(entry))) {
        context.addIssue({
            code: "custom",
            path: ["omitted"],
            message: "Workspace omitted paths must be unique, sorted, and disjoint",
        });
    }
});
export const agentTraceWorkspaceChangeSchema = z
    .object({
    schemaVersion: z.literal(1),
    ordinal: z.number().int().positive(),
    trigger: z
        .object({
        kind: z.enum([
            "tool",
            "final_reconciliation",
            "recovery_reconciliation",
            "finalizer",
        ]),
        toolCallId: traceIdentifierSchema.nullable(),
        toolOutcome: z.enum(["succeeded", "failed", "cancelled"]).nullable(),
    })
        .strict(),
    beforeTreeDigest: agentTraceSha256DigestSchema,
    afterTreeDigest: agentTraceSha256DigestSchema,
    entries: z
        .array(z
        .object({
        path: agentTraceWorkspacePathSchema,
        operation: z.enum(["added", "modified", "deleted"]),
        kind: z.enum(["file", "symlink"]),
        byteSize: z.number().int().nonnegative(),
        beforeDigest: agentTraceSha256DigestSchema.nullable(),
        afterDigest: agentTraceSha256DigestSchema.nullable(),
        contentRef: agentTraceSha256DigestSchema.nullable(),
        availability: z.enum([
            "captured",
            "metadata_only",
            "policy_excluded",
            "deleted",
        ]),
        reason: z.string().trim().min(1).max(500).nullable(),
    })
        .strict())
        .max(50_000),
    omitted: z
        .array(z
        .object({
        path: agentTraceWorkspacePathSchema,
        operation: z.enum(["added", "removed", "changed"]),
        reason: z.string().trim().min(1).max(500),
    })
        .strict())
        .max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    if (value.trigger.kind === "tool" !== Boolean(value.trigger.toolCallId) ||
        value.trigger.kind === "tool" !== Boolean(value.trigger.toolOutcome)) {
        context.addIssue({
            code: "custom",
            path: ["trigger"],
            message: "Only tool-triggered workspace changes identify a tool settlement",
        });
    }
    if (value.entries.length === 0 && value.omitted.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["entries"],
            message: "A workspace change requires a changed file or omission",
        });
    }
    const paths = value.entries.map(({ path }) => path);
    if (new Set(paths).size !== paths.length ||
        paths.some((entry, index) => index > 0 && entry <= paths[index - 1])) {
        context.addIssue({
            code: "custom",
            path: ["entries"],
            message: "Workspace change paths must be unique and sorted",
        });
    }
    for (let index = 0; index < value.entries.length; index += 1) {
        const entry = value.entries[index];
        const issue = (message) => {
            context.addIssue({
                code: "custom",
                path: ["entries", index],
                message,
            });
        };
        if (entry.operation === "added" && entry.beforeDigest !== null) {
            issue("An added workspace entry cannot have a prior digest");
        }
        if (entry.operation === "deleted" &&
            (entry.afterDigest !== null ||
                entry.contentRef !== null ||
                entry.availability !== "deleted" ||
                entry.reason !== null)) {
            issue("A deleted workspace entry cannot retain current content");
        }
        if (entry.operation !== "deleted" && entry.availability === "deleted") {
            issue("Only a deleted workspace entry can use deleted availability");
        }
        if (entry.availability === "captured" &&
            (!entry.afterDigest || entry.contentRef !== entry.afterDigest)) {
            issue("A captured workspace change requires its content reference");
        }
        if (entry.availability === "metadata_only" &&
            (entry.contentRef !== null || !entry.reason)) {
            issue("Metadata-only workspace changes require an unavailable-content reason");
        }
        if (entry.availability === "policy_excluded" &&
            (entry.afterDigest !== null ||
                entry.contentRef !== null ||
                !entry.reason)) {
            issue("Policy-excluded workspace changes cannot retain a digest or content");
        }
    }
    const omittedPaths = value.omitted.map(({ path }) => path);
    if (new Set(omittedPaths).size !== omittedPaths.length ||
        omittedPaths.some((entry, index) => index > 0 && entry <= omittedPaths[index - 1])) {
        context.addIssue({
            code: "custom",
            path: ["omitted"],
            message: "Workspace omission changes must be unique and sorted",
        });
    }
});
export const agentTraceInputDeclaredDataSchema = z
    .object({ input: agentTraceInputIdentitySchema })
    .strict();
export const agentTraceArtifactDeclaredDataSchema = z
    .object({
    artifact: z.union([
        agentTraceContentArtifactSchema,
        agentTraceStoredContentArtifactSchema,
    ]),
})
    .strict();
export const agentTraceWorkspaceSnapshotDataSchema = z
    .object({ snapshot: agentTraceWorkspaceSnapshotSchema })
    .strict();
export const agentTraceWorkspaceChangeDataSchema = z
    .object({ change: agentTraceWorkspaceChangeSchema })
    .strict();
export const agentTraceFinalizedDataSchema = z
    .object({
    termination: agentTraceTerminationSchema,
    recoveredWalEntries: z.number().int().nonnegative(),
    discardedWalEntries: z.number().int().nonnegative(),
    workspaceReconciled: z.boolean(),
})
    .strict();
export const agentTraceManifestSchema = z
    .object({
    conversationExecution: conversationExecutionSchema.optional(),
    schemaVersion: z.literal(AGENT_TRACE_SCHEMA_VERSION),
    format: z.literal(AGENT_TRACE_FORMAT),
    userId: userIdSchema,
    appId: appIdSchema,
    threadId: threadIdSchema,
    rootRunId: runIdSchema,
    correlationId: correlationIdSchema,
    createdAt: isoTimestampSchema,
    initialCommand: agentCommandSchema,
    // Document bodies remain in the protected initial command. This compact
    // identity makes catalog attribution explicit without duplicating them.
    skillCatalog: agentSkillCatalogIdentitySchema.optional(),
    platform: z
        .object({
        version: z.string().trim().min(1).max(100),
        revision: z.string().trim().min(1).max(200),
        environment: z.string().trim().min(1).max(100),
    })
        .strict(),
    runtime: z
        .object({
        adapter: z.string().trim().min(1).max(100),
        provider: z.string().trim().min(1).max(100),
        model: z.string().trim().min(1).max(200).nullable(),
        reasoningEffort: traceReasoningEffortSchema.nullable(),
        workspaceRef: z.string().trim().min(1).max(500),
        checkpointVersion: z.number().int().nonnegative(),
        // Retired per-run token ceiling. Bundles written before its removal
        // (2026-09-21) still carry it and must stay readable and keep their
        // manifest digest; new manifests never set it.
        tokenLimit: z.number().int().min(1_000).nullable().optional(),
        resources: z
            .object({
            memoryBytes: z.number().int().positive(),
            nanoCpus: z.number().int().positive(),
            pidsLimit: z.number().int().positive(),
            maxChildAgents: z.number().int().nonnegative(),
        })
            .strict(),
    })
        .strict(),
    capture: z
        .object({
        liveWriteAheadLog: z.literal(true),
        runtimeEvents: z.literal(true),
        engineEvents: z.literal(true),
        harnessDefinition: z.literal(true),
        adapterRequests: z.literal(true),
        exactProviderRequests: z.boolean(),
        workspaceSnapshots: z.boolean(),
        controlPlaneSpans: z.boolean(),
        // Workspace content artifacts are stored once per user and app and
        // referenced by digest instead of inlined. Manifests created before
        // the content store omit it and keep inline content.
        contentByReference: z.boolean().optional(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.initialCommand.runId !== value.rootRunId ||
        value.initialCommand.type === "run.pause" ||
        value.initialCommand.type === "run.cancel") {
        context.addIssue({
            code: "custom",
            path: ["initialCommand"],
            message: "A trace manifest requires its root run's launch command",
        });
    }
    const commandCatalog = value.initialCommand.type === "run.start" ||
        value.initialCommand.type === "run.continue"
        ? value.initialCommand.skillCatalog
        : undefined;
    if (Boolean(commandCatalog) !== Boolean(value.skillCatalog) ||
        (commandCatalog &&
            JSON.stringify(agentSkillCatalogIdentity(commandCatalog)) !==
                JSON.stringify(value.skillCatalog))) {
        context.addIssue({
            code: "custom",
            path: ["skillCatalog"],
            message: "Trace skill identity differs from its root command snapshot",
        });
    }
});
export const agentTraceEventTypeSchema = z.enum([
    "trace.started",
    "trace.finalized",
    "input.declared",
    "command.received",
    "harness.loaded",
    "engine.request",
    "engine.event",
    "runtime.event",
    "runtime.signal",
    "runtime.failure",
    "workspace.snapshot",
    "workspace.change",
    "control.operation",
    "artifact.declared",
    "artifact.chunk",
]);
export const agentTraceEventSchema = z
    .object({
    schemaVersion: z.literal(AGENT_TRACE_SCHEMA_VERSION),
    format: z.literal(AGENT_TRACE_FORMAT),
    traceEventId: eventIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    parentRunId: runIdSchema.nullable(),
    childRunId: runIdSchema.nullable(),
    sequence: z.number().int().nonnegative(),
    runtimeAttempt: z.number().int().positive(),
    sessionId: traceIdentifierSchema.nullable(),
    invocationId: traceIdentifierSchema.nullable(),
    toolCallId: traceIdentifierSchema.nullable(),
    spanId: traceIdentifierSchema.nullable(),
    parentSpanId: traceIdentifierSchema.nullable(),
    causedBy: eventIdSchema.nullable(),
    occurredAt: isoTimestampSchema,
    elapsedMs: z.number().nonnegative().finite(),
    type: agentTraceEventTypeSchema,
    data: agentTraceJsonSchema,
})
    .strict()
    .superRefine((value, context) => {
    const isRoot = value.runId === value.rootRunId;
    if ((isRoot && (value.parentRunId !== null || value.childRunId !== null)) ||
        (!isRoot &&
            (value.parentRunId !== value.rootRunId || value.childRunId !== value.runId))) {
        context.addIssue({
            code: "custom",
            path: ["runId"],
            message: "Trace root and child lineage is inconsistent",
        });
    }
});
export const agentTracePartialSchema = z
    .object({
    source: z.enum(["engine", "runtime"]),
    rootRunId: runIdSchema,
    runId: runIdSchema,
    parentRunId: runIdSchema.nullable(),
    childRunId: runIdSchema.nullable(),
    sessionId: traceIdentifierSchema.nullable(),
    messageId: traceIdentifierSchema,
    partId: traceIdentifierSchema.nullable(),
    field: traceIdentifierSchema.nullable(),
    firstSequence: z.number().int().nonnegative(),
    lastSequence: z.number().int().nonnegative(),
    firstOccurredAt: isoTimestampSchema,
    lastOccurredAt: isoTimestampSchema,
    deltaCount: z.number().int().positive(),
    // Version-2 writers retain the exact contributing source positions so
    // interleaved message streams can be proven without expanding the cursor.
    // Optionality keeps pre-completeness version-1 bundles schema-readable.
    sourceSequences: z
        .array(z.number().int().nonnegative())
        .max(100_000)
        .optional(),
    content: z.string(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.lastSequence < value.firstSequence) {
        context.addIssue({
            code: "custom",
            path: ["lastSequence"],
            message: "A trace partial cannot end before it starts",
        });
    }
    if (value.sourceSequences &&
        (value.sourceSequences.length !== value.deltaCount ||
            value.sourceSequences[0] !== value.firstSequence ||
            value.sourceSequences.at(-1) !== value.lastSequence ||
            value.sourceSequences.some((sequence, index) => index > 0 && sequence <= value.sourceSequences[index - 1]))) {
        context.addIssue({
            code: "custom",
            path: ["sourceSequences"],
            message: "Trace partial source sequences must be exact and ordered",
        });
    }
    const isRoot = value.runId === value.rootRunId;
    if ((isRoot && (value.parentRunId !== null || value.childRunId !== null)) ||
        (!isRoot &&
            (value.childRunId !== value.runId || value.parentRunId !== value.rootRunId))) {
        context.addIssue({
            code: "custom",
            path: ["runId"],
            message: "Trace partial lineage is inconsistent",
        });
    }
});
export const agentTraceCompletenessCategorySchema = z.enum([
    "input_digests",
    "user_system_inputs",
    "assistant_text",
    "reasoning_summaries",
    "plans_tasks",
    "questions_approvals",
    "tool_activity",
    "usage_provider",
    "child_activity",
    "artifacts",
    "workspace_start",
    "workspace_mutations",
    "workspace_end",
    "control_plane",
    "release_verification",
    "hidden_reasoning",
]);
const agentTraceSequenceRangeSchema = z
    .object({
    firstSequence: z.number().int().nonnegative(),
    lastSequence: z.number().int().nonnegative(),
})
    .strict()
    .refine((value) => value.lastSequence >= value.firstSequence, {
    message: "Trace sequence range cannot end before it starts",
    path: ["lastSequence"],
});
export const agentTraceSequenceGapSchema = z
    .object({
    firstSequence: z.number().int().nonnegative(),
    lastSequence: z.number().int().nonnegative(),
    reason: z.string().trim().min(1).max(500),
})
    .strict()
    .refine((value) => value.lastSequence >= value.firstSequence, {
    message: "Trace sequence gap cannot end before it starts",
    path: ["lastSequence"],
});
export const agentTraceEvidenceGapSchema = z
    .object({
    category: agentTraceCompletenessCategorySchema,
    code: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/),
    detail: z.string().trim().min(1).max(1_000),
})
    .strict();
export const agentTraceCompletenessReportSchema = z
    .object({
    schemaVersion: z.literal(1),
    scope: z.literal("observable-only"),
    status: z.enum(["collecting", "complete", "incomplete"]),
    terminalState: z.enum([
        "running",
        "completed",
        "failed",
        "cancelled",
        "crashed",
        "oom_killed",
        "callback_lost",
        "launcher_restarted",
        "unrecoverable",
    ]),
    generatedAt: isoTimestampSchema,
    checkpointedThroughSequence: z.number().int().min(-1),
    sequenceRanges: z.array(agentTraceSequenceRangeSchema).max(10_000),
    sequenceGaps: z.array(agentTraceSequenceGapSchema).max(10_000),
    evidenceGaps: z.array(agentTraceEvidenceGapSchema).max(1_000),
    categories: z
        .array(z
        .object({
        category: agentTraceCompletenessCategorySchema,
        status: z.enum([
            "captured",
            "partial",
            "unavailable",
            "not_observed",
        ]),
        observedCount: z.number().int().nonnegative(),
        retainedCount: z.number().int().nonnegative(),
        reason: z.string().trim().min(1).max(1_000).nullable(),
    })
        .strict())
        .length(agentTraceCompletenessCategorySchema.options.length),
    counts: z
        .object({
        sourceEvents: z.number().int().nonnegative(),
        storedEvents: z.number().int().nonnegative(),
        partials: z.number().int().nonnegative(),
        artifacts: z.number().int().nonnegative(),
        workspaceSnapshots: z.number().int().nonnegative(),
        workspaceChanges: z.number().int().nonnegative(),
    })
        .strict(),
    hashes: z
        .object({
        manifest: agentTraceSha256DigestSchema,
        eventStream: agentTraceSha256DigestSchema,
        workspaceStart: agentTraceSha256DigestSchema.nullable(),
        workspaceEnd: agentTraceSha256DigestSchema.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    const categoryNames = value.categories.map(({ category }) => category);
    const expectedCategories = new Set(agentTraceCompletenessCategorySchema.options);
    if (new Set(categoryNames).size !== categoryNames.length ||
        categoryNames.some((category) => !expectedCategories.has(category)) ||
        [...expectedCategories].some((category) => !categoryNames.includes(category))) {
        context.addIssue({
            code: "custom",
            path: ["categories"],
            message: "Completeness report must contain each category exactly once",
        });
    }
    if ((value.status === "collecting") !==
        (value.terminalState === "running")) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Only a running trace can remain collecting",
        });
    }
    if (value.status === "complete" &&
        (value.sequenceGaps.length > 0 ||
            value.evidenceGaps.length > 0 ||
            value.categories.some(({ category, status }) => category !== "hidden_reasoning" &&
                (status === "partial" || status === "unavailable")))) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A complete trace cannot contain observable evidence gaps",
        });
    }
});
export const agentTraceBundleSchema = z
    .object({
    schemaVersion: z.literal(AGENT_TRACE_SCHEMA_VERSION),
    format: z.literal(AGENT_TRACE_FORMAT),
    manifest: agentTraceManifestSchema,
    capturedAt: isoTimestampSchema,
    checkpointedThroughSequence: z.number().int().min(-1),
    events: z.array(agentTraceEventSchema),
    partials: z.array(agentTracePartialSchema),
    compaction: z
        .object({
        sourceEventCount: z.number().int().nonnegative(),
        storedEventCount: z.number().int().nonnegative(),
        engineMessageDeltaCount: z.number().int().nonnegative(),
        runtimeMessageDeltaCount: z.number().int().nonnegative(),
        coalescedEventCount: z.number().int().nonnegative().default(0),
        semanticCompactionVersion: z.number().int().nonnegative().default(0),
    })
        .strict(),
    // Bundles produced before complete-trace capture omit this report. New
    // checkpoints always include it, which keeps legacy objects readable
    // without manufacturing historical evidence.
    completeness: agentTraceCompletenessReportSchema.optional(),
    // Only a trace download carries this: the stored content of every
    // stored artifact, keyed by digest, so the file is self-contained. It
    // sits outside the event stream and its hashes. Stored bundles and
    // checkpoints never include it.
    resolvedContents: z
        .record(agentTraceSha256DigestSchema, agentTraceResolvedContentSchema)
        .optional(),
})
    .strict()
    .superRefine((value, context) => {
    const missingSequenceCount = (value.completeness?.sequenceGaps ?? []).reduce((total, gap) => total + gap.lastSequence - gap.firstSequence + 1, 0);
    if (value.compaction.sourceEventCount !==
        value.checkpointedThroughSequence + 1 - missingSequenceCount) {
        context.addIssue({
            code: "custom",
            path: ["compaction", "sourceEventCount"],
            message: "Trace source count does not match its checkpoint cursor",
        });
    }
    if (value.compaction.storedEventCount !== value.events.length) {
        context.addIssue({
            code: "custom",
            path: ["compaction", "storedEventCount"],
            message: "Trace stored-event count does not match its event collection",
        });
    }
    if (value.compaction.sourceEventCount !==
        value.compaction.storedEventCount +
            value.compaction.engineMessageDeltaCount +
            value.compaction.runtimeMessageDeltaCount +
            value.compaction.coalescedEventCount) {
        context.addIssue({
            code: "custom",
            path: ["compaction", "coalescedEventCount"],
            message: "Trace compaction counts do not reconcile with the source count",
        });
    }
    let previousSequence = -1;
    for (let index = 0; index < value.events.length; index += 1) {
        const event = value.events[index];
        if (event.rootRunId !== value.manifest.rootRunId ||
            event.sequence <= previousSequence ||
            event.sequence > value.checkpointedThroughSequence) {
            context.addIssue({
                code: "custom",
                path: ["events", index, "sequence"],
                message: "Trace bundle events must be ordered inside the checkpoint cursor",
            });
            break;
        }
        previousSequence = event.sequence;
    }
    for (let index = 0; index < value.partials.length; index += 1) {
        const partial = value.partials[index];
        if (partial &&
            (partial.rootRunId !== value.manifest.rootRunId ||
                partial.lastSequence > value.checkpointedThroughSequence)) {
            context.addIssue({
                code: "custom",
                path: ["partials", index, "lastSequence"],
                message: "Trace partial exceeds the checkpoint cursor",
            });
            break;
        }
    }
    if (value.completeness) {
        const report = value.completeness;
        if (report.checkpointedThroughSequence !==
            value.checkpointedThroughSequence ||
            report.counts.sourceEvents !== value.compaction.sourceEventCount ||
            report.counts.storedEvents !== value.events.length ||
            report.counts.partials !== value.partials.length) {
            context.addIssue({
                code: "custom",
                path: ["completeness", "counts"],
                message: "Trace completeness counts do not match the bundle",
            });
        }
        const ordered = [...report.sequenceGaps].sort((left, right) => left.firstSequence - right.firstSequence);
        for (let index = 0; index < ordered.length; index += 1) {
            const gap = ordered[index];
            const previous = ordered[index - 1];
            if (!gap ||
                gap.lastSequence > value.checkpointedThroughSequence ||
                (previous && gap.firstSequence <= previous.lastSequence)) {
                context.addIssue({
                    code: "custom",
                    path: ["completeness", "sequenceGaps", index],
                    message: "Trace completeness sequence gaps overlap or exceed the cursor",
                });
                break;
            }
        }
    }
});
export const runtimeTraceConfigSchema = z
    .object({
    format: z.literal(AGENT_TRACE_FORMAT),
    rootRunId: runIdSchema,
    eventSequence: z.number().int().nonnegative(),
    runtimeAttempt: z.number().int().positive(),
    callbackUrl: z.url(),
    manifest: agentTraceManifestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.manifest.rootRunId !== value.rootRunId) {
        context.addIssue({
            code: "custom",
            path: ["manifest", "rootRunId"],
            message: "Runtime trace manifest does not match its root run",
        });
    }
});
export const agentTraceArtifactSchema = z
    .object({
    digest: agentTraceSha256DigestSchema,
    byteSize: z.number().int().nonnegative(),
    mediaType: z.string().trim().min(1).max(200),
})
    .strict();
export const runtimeTraceFinalizerRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    trace: runtimeTraceConfigSchema,
    termination: agentTraceTerminationSchema,
    workspaceAvailable: z.boolean(),
    // Older finalizer requests predate app-owner runtimes and omit this field.
    authorityMode: z
        .enum(["legacy_split_v1", "app_owner_v1"])
        .optional(),
})
    .strict();
export const runtimeTraceFinalizerResultSchema = z
    .object({
    schemaVersion: z.literal(1),
    event: z.literal("agent_trace_finalizer_settled"),
    rootRunId: runIdSchema,
    status: z.enum(["complete", "incomplete"]),
    checkpointedThroughSequence: z.number().int().min(-1),
    sourceEventCount: z.number().int().nonnegative(),
    storedEventCount: z.number().int().nonnegative(),
    gapCount: z.number().int().nonnegative(),
    completenessDigest: agentTraceSha256DigestSchema,
})
    .strict();
/**
 * Verify content-addressed evidence that cannot be checked inside Zod's
 * synchronous parser. Callers accepting or serving a new completeness-aware
 * bundle must use this verifier after schema parsing. Legacy bundles without
 * a completeness report remain schema-readable and intentionally unverifiable.
 */
export async function verifyAgentTraceBundleEvidence(value, options = {}) {
    const bundle = agentTraceBundleSchema.parse(value);
    const report = bundle.completeness;
    if (!report) {
        // Content by reference postdates completeness reports, so only inline
        // content may appear without one. (The initial empty object has none.)
        if (bundle.resolvedContents ||
            bundle.events.some((event) => event.type === "artifact.declared" &&
                traceEvidenceRecord(traceEvidenceRecord(event.data)?.artifact)
                    ?.storage !== undefined)) {
            throw new Error("Content stored by reference requires a completeness report");
        }
        return bundle;
    }
    const byReference = bundle.manifest.capture.contentByReference === true;
    const artifacts = new Map();
    const storedArtifacts = [];
    const snapshots = [];
    const changes = [];
    for (const event of bundle.events) {
        if (event.type === "input.declared") {
            agentTraceInputDeclaredDataSchema.parse(event.data);
        }
        else if (event.type === "artifact.declared") {
            const artifact = agentTraceArtifactDeclaredDataSchema.parse(event.data).artifact;
            if (isStoredTraceContentArtifact(artifact) !== byReference) {
                throw new Error(byReference
                    ? `Trace artifact content must be stored by reference: ${artifact.digest}`
                    : `Trace artifact is stored by reference without the manifest capability: ${artifact.digest}`);
            }
            if (!isStoredTraceContentArtifact(artifact)) {
                const bytes = decodeTraceArtifact(artifact);
                if (bytes.byteLength !== artifact.byteSize ||
                    (await sha256TraceBytes(bytes)) !== artifact.digest) {
                    throw new Error(`Trace artifact content does not match ${artifact.digest}`);
                }
            }
            const existing = artifacts.get(artifact.digest);
            if (existing &&
                canonicalAgentTraceEvidence(existing) !==
                    canonicalAgentTraceEvidence(artifact)) {
                throw new Error(`Trace artifact digest has conflicting content: ${artifact.digest}`);
            }
            if (!existing && isStoredTraceContentArtifact(artifact)) {
                storedArtifacts.push(artifact);
            }
            artifacts.set(artifact.digest, artifact);
        }
        else if (event.type === "workspace.snapshot") {
            snapshots.push(agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot);
        }
        else if (event.type === "workspace.change") {
            changes.push(agentTraceWorkspaceChangeDataSchema.parse(event.data).change);
        }
        else if (event.type === "trace.finalized") {
            agentTraceFinalizedDataSchema.parse(event.data);
        }
    }
    for (const snapshot of snapshots) {
        const expectedTreeDigest = await sha256TraceValue({
            schemaVersion: 1,
            files: snapshot.files,
            omitted: snapshot.omitted,
        });
        if (snapshot.treeDigest !== expectedTreeDigest) {
            throw new Error(`Workspace ${snapshot.kind} tree digest does not match its inventory`);
        }
        let capturedBytes = 0;
        for (const file of snapshot.files) {
            if (file.availability !== "captured")
                continue;
            const artifact = file.contentRef
                ? artifacts.get(file.contentRef)
                : undefined;
            if (!artifact || file.digest !== artifact.digest) {
                throw new Error(`Workspace content reference is unresolved: ${file.path}`);
            }
            if (file.byteSize !== artifact.byteSize) {
                throw new Error(`Workspace content byte size does not match: ${file.path}`);
            }
            capturedBytes += artifact.byteSize;
        }
        if (snapshot.capturedBytes !== capturedBytes) {
            throw new Error(`Workspace ${snapshot.kind} captured byte count is invalid`);
        }
    }
    for (const change of changes) {
        for (const entry of change.entries) {
            if (entry.availability === "captured") {
                const artifact = entry.contentRef
                    ? artifacts.get(entry.contentRef)
                    : undefined;
                if (!artifact || entry.afterDigest !== artifact.digest) {
                    throw new Error(`Workspace change content reference is unresolved: ${entry.path}`);
                }
                if (entry.byteSize !== artifact.byteSize) {
                    throw new Error(`Workspace change byte size does not match: ${entry.path}`);
                }
            }
        }
    }
    verifySequenceCoverage(report);
    verifyRetainedSequenceEvidence(bundle, report);
    verifyCompletenessClaims(bundle, report);
    await verifyWorkspaceTimeline(bundle.events);
    const manifestDigest = await sha256TraceValue(bundle.manifest);
    const eventStreamDigest = await sha256TraceValue({
        events: bundle.events,
        partials: bundle.partials,
    });
    const start = snapshots.find((snapshot) => snapshot.kind === "start") ?? null;
    let end = null;
    for (let index = snapshots.length - 1; index >= 0; index -= 1) {
        if (snapshots[index]?.kind === "end") {
            end = snapshots[index] ?? null;
            break;
        }
    }
    if (report.hashes.manifest !== manifestDigest ||
        report.hashes.eventStream !== eventStreamDigest ||
        report.hashes.workspaceStart !== (start?.treeDigest ?? null) ||
        report.hashes.workspaceEnd !== (end?.treeDigest ?? null)) {
        throw new Error("Trace completeness hashes do not match the bundle");
    }
    if (report.counts.artifacts !== artifacts.size ||
        report.counts.workspaceSnapshots !== snapshots.length ||
        report.counts.workspaceChanges !== changes.length) {
        throw new Error("Trace completeness evidence counts do not reconcile");
    }
    if (bundle.resolvedContents) {
        await verifyResolvedTraceContents(bundle.resolvedContents, storedArtifacts);
    }
    else if (storedArtifacts.length > 0 && options.verifyStoredContent) {
        await options.verifyStoredContent(storedArtifacts);
    }
    return bundle;
}
/** Every stored artifact needs its exact bytes, and nothing else may appear. */
async function verifyResolvedTraceContents(resolved, storedArtifacts) {
    const expected = new Map(storedArtifacts.map((artifact) => [artifact.digest, artifact]));
    for (const digest of Object.keys(resolved)) {
        if (!expected.has(digest)) {
            throw new Error(`Resolved trace content is not referenced: ${digest}`);
        }
    }
    for (const artifact of storedArtifacts) {
        const content = resolved[artifact.digest];
        if (!content || content.encoding !== artifact.encoding) {
            throw new Error(`Resolved trace content is missing: ${artifact.digest}`);
        }
        const bytes = decodeTraceArtifact(content);
        if (bytes.byteLength !== artifact.byteSize ||
            (await sha256TraceBytes(bytes)) !== artifact.digest) {
            throw new Error(`Resolved trace content does not match ${artifact.digest}`);
        }
    }
}
export function isStoredTraceContentArtifact(artifact) {
    return "storage" in artifact;
}
/** The reference form of an inline artifact; its bytes go to the store. */
export function storedTraceContentArtifact(artifact) {
    return {
        digest: artifact.digest,
        byteSize: artifact.byteSize,
        mediaType: artifact.mediaType,
        encoding: artifact.encoding,
        redacted: artifact.redacted,
        storage: AGENT_TRACE_CONTENT_STORAGE,
    };
}
/** The sanitized bytes an inline artifact or resolved content represents. */
export function traceContentBytes(value) {
    return decodeTraceArtifact(value);
}
function verifyCompletenessClaims(bundle, report) {
    const categories = new Map(report.categories.map((category) => [category.category, category]));
    const evidenceCodes = new Set(report.evidenceGaps.map(({ code }) => code));
    const derivedTerminalState = retainedTerminalState(bundle.events);
    if (report.terminalState !== derivedTerminalState) {
        throw new Error("Trace completeness terminal state does not match retained terminal evidence");
    }
    const requireAdvertisedGap = (enabled, category, code) => {
        if (enabled)
            return;
        if (report.status === "complete" ||
            categories.get(category)?.status !== "partial" ||
            !evidenceCodes.has(code)) {
            throw new Error(`Trace completeness does not disclose unavailable ${category} capture`);
        }
    };
    requireAdvertisedGap(bundle.manifest.capture.exactProviderRequests, "usage_provider", "exact_provider_requests_unavailable");
    requireAdvertisedGap(bundle.manifest.capture.workspaceSnapshots, "workspace_start", "workspace_snapshots_disabled");
    requireAdvertisedGap(bundle.manifest.capture.controlPlaneSpans, "control_plane", "control_plane_spans_disabled");
    verifyCategoryClaims(bundle, report, categories, evidenceCodes);
    const hidden = categories.get("hidden_reasoning");
    if (!hidden ||
        hidden.status !== "unavailable" ||
        hidden.observedCount !== 0 ||
        hidden.retainedCount !== 0) {
        throw new Error("Trace completeness fabricates unavailable hidden reasoning");
    }
    const omitted = bundle.events.some((event) => {
        if (event.type !== "workspace.snapshot")
            return false;
        return (agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot.omitted
            .length > 0);
    });
    if (omitted &&
        !evidenceCodes.has("workspace_paths_omitted") &&
        !evidenceCodes.has("workspace_filesystem_race")) {
        throw new Error("Trace completeness omits unavailable workspace paths");
    }
    if (derivedTerminalState !== "running") {
        verifyTerminalEvidenceRequirements(bundle, report, categories);
    }
}
function retainedTerminalState(events) {
    let terminalState = "running";
    for (const event of events) {
        if (event.type === "trace.finalized") {
            const termination = agentTraceFinalizedDataSchema.parse(event.data)
                .termination.kind;
            terminalState =
                termination === "crash"
                    ? "crashed"
                    : termination === "launcher_restart"
                        ? "launcher_restarted"
                        : termination;
            continue;
        }
        if (event.type !== "runtime.event")
            continue;
        const runtimeEvent = traceEvidenceRecord(traceEvidenceRecord(event.data)?.event);
        const type = traceEvidenceText(runtimeEvent?.type);
        if (type === "run.completed" &&
            traceEvidenceText(runtimeEvent?.completionKind) !== "conversation") {
            terminalState = "completed";
        }
        else if (type === "run.failed") {
            terminalState = "failed";
        }
        else if (type === "run.cancelled") {
            terminalState = "cancelled";
        }
    }
    return terminalState;
}
function verifyTerminalEvidenceRequirements(bundle, report, categories) {
    const gapsByCategory = new Map();
    for (const gap of report.evidenceGaps) {
        const values = gapsByCategory.get(gap.category) ?? [];
        values.push(gap.code);
        gapsByCategory.set(gap.category, values);
    }
    const requirePartialGap = (category, message, requiredCode) => {
        const codes = gapsByCategory.get(category) ?? [];
        if (report.status !== "incomplete" ||
            categories.get(category)?.status !== "partial" ||
            codes.length === 0 ||
            (requiredCode !== undefined && !codes.includes(requiredCode))) {
            throw new Error(message);
        }
    };
    const inputs = bundle.events.flatMap((event) => {
        if (event.type !== "input.declared")
            return [];
        return [agentTraceInputDeclaredDataSchema.parse(event.data).input];
    });
    const missingInputs = missingAgentTraceInputIdentities(inputs);
    if (missingInputs.length > 0) {
        requirePartialGap("input_digests", "Terminal trace is missing immutable input identities without an explicit gap", "missing_input_digest");
    }
    let hasStart = false;
    let hasEnd = false;
    for (const event of bundle.events) {
        if (event.type !== "workspace.snapshot")
            continue;
        const kind = agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot
            .kind;
        if (kind === "start")
            hasStart = true;
        if (kind === "end")
            hasEnd = true;
    }
    if (!hasStart) {
        requirePartialGap("workspace_start", "Terminal trace is missing its workspace start without an explicit gap");
    }
    if (!hasEnd) {
        requirePartialGap("workspace_end", "Terminal trace is missing its workspace end without an explicit gap");
    }
}
function verifyCategoryClaims(bundle, report, categories, evidenceCodes) {
    const derived = retainedCategoryCounts(bundle);
    const fallback = evidenceCodes.has("trace_finalizer_unavailable");
    const fallbackCounts = fallback ? legacyFallbackCategoryCounts(bundle) : null;
    for (const categoryName of agentTraceCompletenessCategorySchema.options) {
        if (categoryName === "hidden_reasoning")
            continue;
        const category = categories.get(categoryName);
        const count = derived.get(categoryName) ?? {
            observedCount: 0,
            retainedCount: 0,
        };
        if (!category || category.retainedCount > category.observedCount) {
            throw new Error(`Trace completeness category counts are invalid: ${categoryName}`);
        }
        if (fallback) {
            // A service-side fallback may be built from a legacy bundle whose prior
            // category semantics are unavailable. Accept either the exact native
            // materialization or the deterministic legacy fallback projection,
            // never arbitrary inflated or suppressed counts.
            const legacyCount = fallbackCounts?.get(categoryName) ?? 0;
            const matchesNative = category.observedCount === count.observedCount &&
                category.retainedCount === count.retainedCount;
            const matchesLegacy = category.observedCount === legacyCount &&
                category.retainedCount === legacyCount;
            if (!matchesNative && !matchesLegacy) {
                throw new Error(`Trace fallback category counts do not reconcile: ${categoryName}`);
            }
        }
        else if (category.observedCount !== count.observedCount ||
            category.retainedCount !== count.retainedCount) {
            throw new Error(`Trace completeness category counts do not reconcile: ${categoryName}`);
        }
        const hasGap = report.evidenceGaps.some((gap) => gap.category === categoryName);
        if (hasGap && category.status !== "partial") {
            throw new Error(`Trace completeness category does not disclose its gap: ${categoryName}`);
        }
        if (!hasGap && !fallback) {
            const expectedStatus = count.observedCount > 0 ? "captured" : "not_observed";
            if (category.status !== expectedStatus) {
                throw new Error(`Trace completeness category status does not match evidence: ${categoryName}`);
            }
        }
        else if (!hasGap &&
            fallback &&
            ((category.status === "captured" && category.retainedCount === 0) ||
                (category.status === "not_observed" && category.observedCount > 0))) {
            throw new Error(`Trace fallback category status does not match evidence: ${categoryName}`);
        }
    }
}
function legacyFallbackCategoryCounts(bundle) {
    const values = new Map();
    const countEvents = (predicate) => bundle.events.filter(predicate).length;
    values.set("input_digests", 1 + countEvents(({ type }) => type === "input.declared"));
    values.set("user_system_inputs", 1 + countEvents(({ type }) => type === "command.received"));
    values.set("assistant_text", bundle.partials.length);
    values.set("tool_activity", countEvents(({ toolCallId }) => toolCallId !== null));
    values.set("child_activity", countEvents(({ runId, rootRunId }) => runId !== rootRunId));
    values.set("artifacts", countEvents(({ type }) => type === "artifact.declared"));
    values.set("workspace_start", countEvents((event) => event.type === "workspace.snapshot" &&
        agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot.kind ===
            "start"));
    values.set("workspace_mutations", countEvents(({ type }) => type === "workspace.change"));
    values.set("workspace_end", countEvents((event) => event.type === "workspace.snapshot" &&
        agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot.kind ===
            "end"));
    values.set("control_plane", countEvents(({ type }) => type === "control.operation"));
    return values;
}
function retainedCategoryCounts(bundle) {
    const counts = new Map();
    const add = (category, observedCount) => {
        const previous = counts.get(category) ?? {
            observedCount: 0,
            retainedCount: 0,
        };
        counts.set(category, {
            observedCount: previous.observedCount + observedCount,
            retainedCount: previous.retainedCount + 1,
        });
    };
    for (const event of bundle.events) {
        const sourceCount = traceEvidenceCompactedSourceCount(event);
        for (const category of retainedEventCategories(event)) {
            add(category, sourceCount);
        }
    }
    for (const partial of bundle.partials) {
        add("assistant_text", partial.deltaCount);
    }
    return counts;
}
function retainedEventCategories(event) {
    if (event.type === "input.declared")
        return ["input_digests"];
    if (event.type === "command.received" || event.type === "engine.request") {
        return ["user_system_inputs"];
    }
    if (event.type === "artifact.declared" || event.type === "artifact.chunk") {
        return ["artifacts"];
    }
    if (event.type === "workspace.snapshot") {
        const kind = agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot
            .kind;
        return [
            kind === "end"
                ? "workspace_end"
                : kind === "recovery"
                    ? "workspace_mutations"
                    : "workspace_start",
        ];
    }
    if (event.type === "workspace.change")
        return ["workspace_mutations"];
    if (event.type === "control.operation")
        return ["control_plane"];
    if (event.type === "engine.event") {
        const encoded = JSON.stringify(event.data).toLowerCase();
        const categories = [];
        if (encoded.includes("reasoning"))
            categories.push("reasoning_summaries");
        if (encoded.includes("tool") || encoded.includes("commandexecution")) {
            categories.push("tool_activity");
        }
        if (encoded.includes("plan"))
            categories.push("plans_tasks");
        if (encoded.includes("approval") || encoded.includes("question")) {
            categories.push("questions_approvals");
        }
        if (encoded.includes("usage") || encoded.includes("ratelimit")) {
            categories.push("usage_provider");
        }
        if (event.childRunId || encoded.includes("subagent")) {
            categories.push("child_activity");
        }
        if (encoded.includes("agentmessage") ||
            encoded.includes("message.part")) {
            categories.push("assistant_text");
        }
        return [...new Set(categories)];
    }
    if (event.type !== "runtime.event")
        return [];
    const type = traceEvidenceText(traceEvidenceRecord(traceEvidenceRecord(event.data)?.event)?.type);
    if (type === "message.delta" || type === "message.completed") {
        return ["assistant_text"];
    }
    if (type === "tool.started" || type === "tool.completed") {
        return ["tool_activity"];
    }
    if (type === "task_list.updated")
        return ["plans_tasks"];
    if (type === "user.input.required")
        return ["questions_approvals"];
    if (type === "usage.reported" || type?.startsWith("provider.")) {
        return ["usage_provider"];
    }
    if (type?.startsWith("child."))
        return ["child_activity"];
    if (type === "harness.outcome" || type === "checkpoint.committed") {
        return ["release_verification"];
    }
    return [];
}
function traceEvidenceCompactedSourceCount(event) {
    return (traceEvidenceInteger(traceEvidenceRecord(traceEvidenceRecord(event.data)?._opencloudTraceCompaction)?.sourceEventCount) ?? 1);
}
export function canonicalAgentTraceEvidence(value) {
    if (value === null || typeof value !== "object") {
        const encoded = JSON.stringify(value);
        if (encoded === undefined)
            throw new Error("Trace evidence is not JSON");
        return encoded;
    }
    if (Array.isArray(value)) {
        return `[${value.map((entry) => canonicalAgentTraceEvidence(entry)).join(",")}]`;
    }
    return `{${Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalAgentTraceEvidence(entry)}`)
        .join(",")}}`;
}
async function sha256TraceValue(value) {
    return sha256TraceBytes(new TextEncoder().encode(canonicalAgentTraceEvidence(value)));
}
async function sha256TraceBytes(bytes) {
    const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return `sha256:${[...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")}`;
}
function decodeTraceArtifact(artifact) {
    if (artifact.encoding === "utf8") {
        return new TextEncoder().encode(artifact.content);
    }
    const decoded = globalThis.atob(artifact.content);
    return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}
function verifySequenceCoverage(report) {
    const coverage = [
        ...report.sequenceRanges.map((range) => ({ ...range, kind: "range" })),
        ...report.sequenceGaps.map((gap) => ({ ...gap, kind: "gap" })),
    ].sort((left, right) => left.firstSequence - right.firstSequence);
    if (report.checkpointedThroughSequence === -1) {
        if (coverage.length !== 0) {
            throw new Error("An empty trace cannot contain sequence coverage");
        }
        return;
    }
    let expected = 0;
    for (const entry of coverage) {
        if (entry.firstSequence !== expected ||
            entry.lastSequence < entry.firstSequence) {
            throw new Error("Trace completeness sequence coverage is not contiguous");
        }
        expected = entry.lastSequence + 1;
    }
    if (expected !== report.checkpointedThroughSequence + 1) {
        throw new Error("Trace completeness sequence coverage does not reach its cursor");
    }
}
function verifyRetainedSequenceEvidence(bundle, report) {
    const exactCoverage = bundle.compaction.semanticCompactionVersion >=
        AGENT_TRACE_COMPACTION_VERSION;
    const exactSequences = [];
    const overlapsGap = (first, last) => report.sequenceGaps.some((gap) => first <= gap.lastSequence && last >= gap.firstSequence);
    let representedSourceEvents = 0;
    for (const event of bundle.events) {
        const data = traceEvidenceRecord(event.data);
        const summary = traceEvidenceRecord(data?._opencloudTraceCompaction);
        if (!summary) {
            if (overlapsGap(event.sequence, event.sequence)) {
                throw new Error("A retained trace event overlaps a declared sequence gap");
            }
            representedSourceEvents += 1;
            exactSequences.push(event.sequence);
            continue;
        }
        const sourceEventCount = traceEvidenceInteger(summary.sourceEventCount);
        const firstSequence = traceEvidenceInteger(summary.firstSequence);
        const lastSequence = traceEvidenceInteger(summary.lastSequence);
        const sourceSequences = traceEvidenceSequenceList(summary.sourceSequences);
        if (sourceEventCount === null ||
            sourceEventCount < 2 ||
            firstSequence === null ||
            lastSequence === null ||
            firstSequence > event.sequence ||
            lastSequence < event.sequence ||
            lastSequence < firstSequence ||
            sourceEventCount > lastSequence - firstSequence + 1 ||
            overlapsGap(firstSequence, lastSequence)) {
            throw new Error("A retained trace compaction summary is inconsistent");
        }
        if (exactCoverage &&
            (!sourceSequences ||
                sourceSequences.length !== sourceEventCount ||
                sourceSequences[0] !== firstSequence ||
                sourceSequences.at(-1) !== lastSequence ||
                !sourceSequences.includes(event.sequence))) {
            throw new Error("A retained trace compaction summary lacks exact source sequences");
        }
        if (sourceSequences)
            exactSequences.push(...sourceSequences);
        representedSourceEvents += sourceEventCount;
    }
    let engineDeltas = 0;
    let runtimeDeltas = 0;
    for (const partial of bundle.partials) {
        if (partial.deltaCount > partial.lastSequence - partial.firstSequence + 1 ||
            overlapsGap(partial.firstSequence, partial.lastSequence)) {
            throw new Error("A retained trace partial overlaps unavailable evidence");
        }
        if (exactCoverage && !partial.sourceSequences) {
            throw new Error("A retained trace partial lacks exact source sequences");
        }
        if (partial.sourceSequences)
            exactSequences.push(...partial.sourceSequences);
        representedSourceEvents += partial.deltaCount;
        if (partial.source === "engine")
            engineDeltas += partial.deltaCount;
        else
            runtimeDeltas += partial.deltaCount;
    }
    if (representedSourceEvents !== bundle.compaction.sourceEventCount ||
        engineDeltas !== bundle.compaction.engineMessageDeltaCount ||
        runtimeDeltas !== bundle.compaction.runtimeMessageDeltaCount) {
        throw new Error("Retained trace evidence does not represent every source event");
    }
    if (exactCoverage) {
        verifyExactRetainedSequenceCoverage(exactSequences, report.sequenceRanges, bundle.compaction.sourceEventCount, report.checkpointedThroughSequence);
    }
    else if (report.status === "complete") {
        throw new Error("Legacy count-only trace coverage cannot claim complete evidence");
    }
}
const MAX_EXACT_TRACE_SEQUENCE_EVIDENCE = 1_000_000;
function traceEvidenceSequenceList(value) {
    if (!Array.isArray(value) ||
        value.length === 0 ||
        value.length > 100_000 ||
        value.some((sequence, index) => !Number.isSafeInteger(sequence) ||
            sequence < 0 ||
            (index > 0 && sequence <= value[index - 1]))) {
        return null;
    }
    return value;
}
function verifyExactRetainedSequenceCoverage(sourceSequences, claimedRanges, sourceEventCount, checkpointedThroughSequence) {
    if (sourceSequences.length !== sourceEventCount ||
        sourceSequences.length > MAX_EXACT_TRACE_SEQUENCE_EVIDENCE) {
        throw new Error("Exact retained trace sequence evidence is unbounded or incomplete");
    }
    sourceSequences.sort((left, right) => left - right);
    const retainedRanges = [];
    for (const sequence of sourceSequences) {
        if (sequence > checkpointedThroughSequence) {
            throw new Error("Exact retained trace sequence exceeds its cursor");
        }
        const current = retainedRanges.at(-1);
        if (current && sequence <= current.lastSequence) {
            throw new Error("Exact retained trace evidence overlaps or duplicates a sequence");
        }
        if (current && sequence === current.lastSequence + 1) {
            current.lastSequence = sequence;
        }
        else {
            retainedRanges.push({
                firstSequence: sequence,
                lastSequence: sequence,
            });
        }
    }
    if (retainedRanges.length !== claimedRanges.length ||
        retainedRanges.some((range, index) => range.firstSequence !== claimedRanges[index]?.firstSequence ||
            range.lastSequence !== claimedRanges[index]?.lastSequence)) {
        throw new Error("Exact retained trace evidence omits a claimed sequence");
    }
}
async function verifyWorkspaceTimeline(events) {
    let currentTree = null;
    let currentFiles = null;
    let currentOmitted = null;
    let startSeen = false;
    let endSeen = false;
    let changeOrdinal = 0;
    for (const event of events) {
        if (event.type === "workspace.snapshot") {
            const snapshot = agentTraceWorkspaceSnapshotDataSchema.parse(event.data).snapshot;
            if (snapshot.kind === "start") {
                if (startSeen || currentTree !== null || endSeen) {
                    throw new Error("Workspace trace contains more than one start snapshot");
                }
                startSeen = true;
                currentTree = snapshot.treeDigest;
                currentFiles = new Map(snapshot.files.map((file) => [file.path, file]));
                currentOmitted = new Map(snapshot.omitted.map((omission) => [omission.path, omission]));
            }
            else if (snapshot.kind === "recovery") {
                if (!startSeen ||
                    endSeen ||
                    snapshot.previousTreeDigest !== currentTree) {
                    throw new Error("Workspace recovery snapshot does not chain to its tree");
                }
                currentTree = snapshot.treeDigest;
                currentFiles = new Map(snapshot.files.map((file) => [file.path, file]));
                currentOmitted = new Map(snapshot.omitted.map((omission) => [omission.path, omission]));
            }
            else {
                if (!startSeen ||
                    endSeen ||
                    snapshot.treeDigest !== currentTree ||
                    !currentFiles ||
                    !currentOmitted ||
                    canonicalAgentTraceEvidence(snapshot.files) !==
                        canonicalAgentTraceEvidence(sortedMapValues(currentFiles)) ||
                    canonicalAgentTraceEvidence(snapshot.omitted) !==
                        canonicalAgentTraceEvidence(sortedMapValues(currentOmitted))) {
                    throw new Error("Workspace end snapshot does not chain to its tree");
                }
                endSeen = true;
            }
        }
        else if (event.type === "workspace.change") {
            const change = agentTraceWorkspaceChangeDataSchema.parse(event.data).change;
            if (!startSeen ||
                endSeen ||
                !currentFiles ||
                !currentOmitted ||
                change.ordinal !== changeOrdinal + 1 ||
                change.beforeTreeDigest !== currentTree) {
                throw new Error("Workspace change does not chain to its prior tree");
            }
            for (const entry of change.entries) {
                const previous = currentFiles.get(entry.path);
                if ((entry.operation === "added" && previous) ||
                    (entry.operation !== "added" && !previous) ||
                    (previous && entry.beforeDigest !== previous.digest)) {
                    throw new Error(`Workspace change operation does not match its prior inventory: ${entry.path}`);
                }
                if (entry.operation === "deleted") {
                    if (!previous ||
                        entry.kind !== previous.kind ||
                        entry.byteSize !== previous.byteSize) {
                        throw new Error(`Workspace deletion metadata does not match its prior inventory: ${entry.path}`);
                    }
                    currentFiles.delete(entry.path);
                    continue;
                }
                if (entry.availability === "deleted") {
                    throw new Error(`Workspace retained entry has deleted availability: ${entry.path}`);
                }
                const next = {
                    path: entry.path,
                    kind: entry.kind,
                    byteSize: entry.byteSize,
                    digest: entry.afterDigest,
                    contentRef: entry.contentRef,
                    availability: entry.availability,
                    reason: entry.reason,
                };
                if (previous &&
                    canonicalAgentTraceEvidence(previous) ===
                        canonicalAgentTraceEvidence(next)) {
                    throw new Error(`Workspace modification does not change its inventory: ${entry.path}`);
                }
                currentFiles.set(entry.path, next);
            }
            for (const omission of change.omitted) {
                const previous = currentOmitted.get(omission.path);
                if ((omission.operation === "added" && previous) ||
                    (omission.operation !== "added" && !previous)) {
                    throw new Error(`Workspace omission change does not match its prior inventory: ${omission.path}`);
                }
                if (omission.operation === "removed") {
                    if (previous?.reason !== omission.reason) {
                        throw new Error(`Workspace omission removal metadata is invalid: ${omission.path}`);
                    }
                    currentOmitted.delete(omission.path);
                }
                else {
                    if (omission.operation === "changed" &&
                        previous?.reason === omission.reason) {
                        throw new Error(`Workspace omission change is a no-op: ${omission.path}`);
                    }
                    currentOmitted.set(omission.path, {
                        path: omission.path,
                        reason: omission.reason,
                    });
                }
            }
            if ([...currentFiles.keys()].some((entryPath) => currentOmitted?.has(entryPath))) {
                throw new Error("Workspace change inventory overlaps omitted paths");
            }
            const calculatedTree = await sha256TraceValue({
                schemaVersion: 1,
                files: sortedMapValues(currentFiles),
                omitted: sortedMapValues(currentOmitted),
            });
            if (calculatedTree !== change.afterTreeDigest) {
                throw new Error("Workspace change does not produce its claimed tree");
            }
            changeOrdinal = change.ordinal;
            currentTree = change.afterTreeDigest;
        }
    }
}
function sortedMapValues(values) {
    // Match the snapshot schema and scanner's code-unit order, independent of
    // locale. In particular, README.md sorts before app.ts in the stored tree.
    return [...values.values()].sort((left, right) => left.path < right.path ? -1 : left.path > right.path ? 1 : 0);
}
function traceEvidenceRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : null;
}
function traceEvidenceInteger(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 0
        ? value
        : null;
}
function traceEvidenceText(value) {
    return typeof value === "string" ? value : null;
}
//# sourceMappingURL=traces.js.map